import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { display, variant } from 'styled-system'
import { defaultValues } from 'ui/acro/typography/P'
import { P } from 'ui/acro/typography/P'
import { Field as FormikField } from 'formik'
import Collapse from 'ui/acro/animations/Collapse'
import FieldSet from 'ui/acro/elements/forms/FieldSet'
import FieldFeedback from 'ui/acro/elements/forms/FieldFeedback'
import FieldError from 'ui/acro/elements/forms/FieldError'
import Input from 'ui/acro/elements/forms/Input'
import { Box } from 'ui/bend'
import { ResponsiveSVG } from '../elements'
import { Hide, Show } from 'images/latest'
import {
  lowercaseCharacterRegex,
  numberCharacterRegex,
  uppercaseCharacterRegex
} from 'components_v2/Subscribe/utils'

const CustomFieldSet = styled(FieldSet)`
  .error {
    p {
      margin: 0;
    }
    div {
      height: auto;
    }
  }
`

const PasswordInputField = styled(Input)((props) =>
  variant({
    variants: {
      primary: {
        ...defaultValues,
        color: props.theme.colors.grey6,
        width: '100%',
        maxHeight: '50px',
        background: props.theme.colors.base,
        borderColor: props.theme.colors.grey3,
        borderStyle: 'solid',
        borderWidth: '1px',
        marginTop: '8px',
        borderRadius: '0px',
        padding: '12px 16px 11px 16px',
        transition: 'background .25s ease-in-out, border .25s ease-in-out',
        '&:focus': {
          borderColor: props.theme.colors.primary,
          outline: '0',
          background: '#fff'
        },
        '&:focus + label': {
          top: '1px',
          left: '10px',
          fontSize: '14px',
          opacity: 1,
          background: '#fff',
          padding: '0px 6px',
          display: 'block'
        },
        '&:not(:placeholder-shown) + label': {
          top: '1px',
          left: '10px',
          fontSize: '14px',
          opacity: 1,
          background: '#fff',
          padding: '0px 6px',
          display: 'block'
        },
        '&.error': {
          borderColor: props.theme.colors.errorDefault
        }
      }
    }
  })
)

const PlaceHolderLabel = styled(P)``

PlaceHolderLabel.defaultProps = {
  as: 'label',
  variant: 'semibold',
  position: 'absolute',
  transition: '0.8s ease all',
  left: '55px',
  top: '56px',
  fontFamily: 'Proxima-Nova',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.352px',
  color: '#636363',
  display: 'none'
}

const Label = styled(P)``

Label.defaultProps = {
  as: 'label',
  color: 'grey6',
  variant: 'semibold',
  width: '100%',
  display: 'inline-block'
}

const FloatingLabelPassword = ({
  labelTitle,
  name,
  showValidations,
  values,
  errors,
  touched,
  onBlur = () => {},
  handleChange,
  placeHolder
}) => {
  // We use separate focus states to only show the error state for password length on unfocus.
  const [passwordFocus, setPasswordFocus] = useState(false)
  const [passwordConfirmationFocus, setPasswordConfirmationFocus] =
    useState(false)
  const [passwordType, setPasswordType] = useState('password')
  const [isFocused, setIsFocused] = useState(false)

  // This component now supports two types of password inputs - one for a regular password validation, and an additional 'confirm password' input for pages where we ask the user to retype their password. When we rewrite the settings pages to React, we should use both of these inputs like we do on ChooseNewPassword.js. To switch between the two, use prop name='password' for regular PW input, and name='password_confirmation' for the confirmation input.
  const passwordInput = name === 'password'
  const confirmPasswordInput = name === 'password_confirmation'

  // hide/show icon to toggle password visibility
  const togglePasswordType = () => {
    if (passwordType === 'password') {
      setPasswordType('text')
      return
    }
    setPasswordType('password')
  }

  const passwordValidations = (pw, error) => {
    if (passwordInput && showValidations) {
      return (
        <>
          <FieldFeedback type={pw.length >= 8 ? 'success' : 'rest'}>
            8 or more characters
          </FieldFeedback>
          <FieldFeedback
            type={
              lowercaseCharacterRegex.test(pw) === true ? 'success' : 'rest'
            }
          >
            At least 1 lowercase letter
          </FieldFeedback>
          <FieldFeedback
            type={
              uppercaseCharacterRegex.test(pw) === true ? 'success' : 'rest'
            }
          >
            At least 1 uppercase letter
          </FieldFeedback>
          <FieldFeedback
            type={numberCharacterRegex.test(pw) === true ? 'success' : 'rest'}
          >
            At least 1 number
          </FieldFeedback>
        </>
      )
    } else if (confirmPasswordInput && showValidations) {
      if (values.password_confirmation.length >= 1) {
        return (
          <FieldFeedback
            type={error === 'Passwords do not match' ? 'rest' : 'success'}
          >
            Passwords need to match
          </FieldFeedback>
        )
      }
    }
  }

  if (passwordInput) {
    return (
      <CustomFieldSet>
        <Label position='relative'>
          {labelTitle}
          <PasswordInputField
            as={FormikField}
            type={passwordType}
            name={name}
            autoComplete='new-password'
            onKeyUp={handleChange}
            className={
              touched.password &&
              errors.password &&
              (values.password === '' || !passwordFocus) &&
              'error'
            }
            onFocus={() => {
              setPasswordFocus(!passwordFocus)
              setIsFocused(true)
            }}
            onBlur={(e) => {
              setPasswordFocus(!passwordFocus)
              setIsFocused(false)
              onBlur(e)
            }}
            placeholder={isFocused ? '' : placeHolder}
          />
          <PlaceHolderLabel for={name}>
            {placeHolder && placeHolder}
          </PlaceHolderLabel>
          <Box
            position='absolute'
            top={!labelTitle ? '22px' : '46px'}
            right='4%'
            cursor='pointer'
            zIndex='1'
            onClick={togglePasswordType}
          >
            <ResponsiveSVG
              SVG={passwordType === 'password' ? Show : Hide}
              width='20px'
              height='20px'
            />
          </Box>
        </Label>
        <Collapse isOpened={passwordFocus}>
          {passwordValidations(values.password, errors.password)}
        </Collapse>
        <Collapse isOpened={touched.password && errors.password}>
          {touched.password && errors.password && (
            <FieldError className='error'>{errors.password}</FieldError>
          )}
        </Collapse>
      </CustomFieldSet>
    )
  } else if (confirmPasswordInput) {
    return (
      <CustomFieldSet>
        <Label position='relative'>
          {labelTitle}
          <Input
            as={FormikField}
            type={passwordType}
            name={name}
            className={
              touched.password_confirmation &&
              errors.password_confirmation &&
              (values.password_confirmation === '' ||
                !passwordConfirmationFocus) &&
              'error'
            }
            onFocus={() => {
              setPasswordConfirmationFocus(!passwordConfirmationFocus)
            }}
            onBlur={(e) => {
              setPasswordConfirmationFocus(!passwordConfirmationFocus)
              onBlur(e)
            }}
          />
          <Box
            position='absolute'
            top='46px'
            right='4%'
            cursor='pointer'
            zIndex='1'
            onClick={togglePasswordType}
          >
            <ResponsiveSVG
              SVG={passwordType === 'password' ? Show : Hide}
              width='20px'
              height='20px'
            />
          </Box>
        </Label>
        <Collapse isOpened={passwordConfirmationFocus}>
          {passwordValidations(
            values.password_confirmation,
            errors.password_confirmation
          )}
        </Collapse>
        <Collapse
          isOpened={
            touched.password_confirmation &&
            errors.password_confirmation &&
            !passwordConfirmationFocus
          }
        >
          {touched.password_confirmation && errors.password_confirmation && (
            <FieldError>{errors.password_confirmation}</FieldError>
          )}
        </Collapse>
      </CustomFieldSet>
    )
  }
}

FloatingLabelPassword.propTypes = {
  labelTitle: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  showValidations: PropTypes.bool.isRequired,
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  onBlur: PropTypes.func,
  handleChange: PropTypes.func
}

export default FloatingLabelPassword
